import React, { useEffect, useState } from "react";
import classes from "./Blog.module.scss";
import { Link } from "react-router";
import { Link as ScrollLink } from "react-scroll";
import noise from "../../../media/noise.png";
import quoteStartIcon from "../../../media/quote-start.svg";
import quoteEndIcon from "../../../media/quote-end.svg";
import FormCard from "../../../components/formCard/FormCard.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../../../constants/strapi-base-url.constant.ts";
import { fetchLocalizedEntry } from "../../../constants/cms-locale.constant.ts";
import { useTranslation } from "react-i18next";
import ClipLoader from "react-spinners/ClipLoader";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface EContent {
  imageBlock: {
    title: string | null;
    text: string | null;
    quote: string | null;
    image: string | null;
  };
  ctaBlock: { title: string | null; text: string | null; buttonText: string };
  videoBlock: {
    block1: { title: string | null; text: string | null } | null;
    video: string | null;
    block2: { title: string | null; text: string | null } | null;
  };
  markdown?: string
}

const Blog = () => {
  const [loading, setLoading] = useState(false);
  const [author, setAuthor] = useState<{
    name: string;
    avatar: string;
    id: string;
  } | null>(null);
  const [heading, setHeading] = useState<{
    title: string;
    message: string;
    publishedAt: string;
    topicName: string;
  } | null>(null);
  const [content, setContent] = useState<EContent | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const getBlog = async () => {
    setLoading(true);
    const dateConvertOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };

    const populate =
      "populate[0]=full_info&populate[1]=full_info.blog_author&populate[2]=full_info.image_block&populate[3]=full_info.video_block&populate[4]=full_info.video_block.text_block_1&populate[5]=full_info.video_block.text_block_2&populate[6]=full_info.video_block.video&populate[7]=full_info.image_block.image&populate[8]=full_info.blog_author.blog_author.Avatar&populate[9]=full_info.cta_block&populate[10]=blog_preview";

    try {
      let blog = await fetchLocalizedEntry(
        `blogs?filters[slug][$eq]=${encodeURIComponent(id)}&${populate}`,
        i18n.language,
      );

      // Old links used documentId instead of slug
      if (!blog) {
        blog = await fetchLocalizedEntry(`blogs/${id}?${populate}`, i18n.language);
        if (blog?.slug) navigate(`/blogs/${blog.slug}`, { replace: true });
      }

      const info = blog.full_info;
      const authorInfo = info.blog_author?.blog_author;
      setAuthor(
        authorInfo
          ? {
              avatar: authorInfo.Avatar ? `${baseUrl}${authorInfo.Avatar.url}` : null,
              name: authorInfo.Name,
              id: info.blog_author.documentId,
            }
          : null,
      );

      setHeading({
        title: info.title,
        message: info.message,
        publishedAt: new Date(blog.publishedAt).toLocaleDateString(
          i18n.language === "ua" ? "uk-UA" : "en-US",
          dateConvertOptions,
        ),
        topicName: blog.blog_preview.title,
      });

      setContent({
        imageBlock: {
          title: info.image_block?.title ?? null,
          text: info.image_block?.text ?? null,
          quote: info.image_block?.quote ?? null,
          image: info.image_block?.image?.url
            ? `${baseUrl}${info.image_block.image.url}`
            : null,
        },
        ctaBlock: {
          title: info.cta_block?.title ?? null,
          text: info.cta_block?.text ?? null,
          buttonText: info.cta_block?.button_text ?? null,
        },
        videoBlock: {
          video: info.video_block?.video?.url
            ? `${baseUrl}${info.video_block.video.url}`
            : null,
          block1: info.video_block?.text_block_1 ?? null,
          block2: info.video_block?.text_block_2 ?? null,
        },
        markdown: info.content,
      });
    } catch {
      setContent(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getBlog();
  }, [id, i18n.language]);

  if (loading)
    return (
      <main id="nav-dark" className={classes["loading-container"]}>
        <ClipLoader size={120} color="#182347" />
      </main>
    );

  return (
    <main>
      <section className={classes["heading-container"]} id="nav-dark">
        <div className={`${classes["circle"]} ${classes["circle--4"]}`}></div>
        <div className={`${classes["circle"]} ${classes["circle--3"]}`}></div>
        <div className={`${classes["circle"]} ${classes["circle--2"]}`}></div>
        <div className={`${classes["circle"]} ${classes["circle--1"]}`}></div>
        <img
          src={noise}
          alt={"noise background"}
          className={classes["noise"]}
        />
        <div className={classes.container}>
          {heading && (
            <div className={classes.heading}>
              {heading?.topicName && (
                <div className={classes.path}>
                  <Link to="/">{t("blog.breadcrumbHome")}</Link> /{" "}
                  <Link to="/blogs">{t("blog.breadcrumbBlog")}</Link> /{" "}
                  {heading.topicName}
                </div>
              )}
              <h1 className={classes.title}>{heading.title}</h1>
              {heading.message && (
                <p className={classes.text}>{heading.message}</p>
              )}
              <div className={classes.info}>
                <span className={classes["published_at"]}>
                  {t("blog.published")} {heading.publishedAt}
                </span>
                {author && (
                  <div className={classes["author-info"]}>
                    <span>{t("blog.author")} </span>
                    {author.avatar && (
                      <img
                        alt="author avatar"
                        src={author.avatar}
                        className={classes.avatar}
                      />
                    )}
                    {author.name && (
                      <Link to={`/authors/${author.id}`}>{author.name}</Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {content?.imageBlock && (
        <section className="section--white">
          <div className={classes["content-container"]}>
            {content.imageBlock && (
              <div className={classes["text-container"]}>
                {content.imageBlock.title && (
                  <h3 className={classes.title}>{content.imageBlock.title}</h3>
                )}
                {content.imageBlock.text && (
                  <div className={classes.text}>{content.imageBlock.text}</div>
                )}
              </div>
            )}

            {content.imageBlock.quote && (
              <div className={classes["testimonial-container"]}>
                <img
                  className={classes["quote-icon"]}
                  alt="Quote icon"
                  src={quoteStartIcon}
                />
                <span className={classes.testimonial}>
                  {content.imageBlock.quote}
                </span>
                <img
                  className={classes["quote-icon"]}
                  alt="Quote icon"
                  src={quoteEndIcon}
                />
              </div>
            )}

            {content.imageBlock.image && (
              <div className={classes["image-container"]}>
                <img src={content.imageBlock.image} alt="" />
              </div>
            )}
          </div>
        </section>
      )}

      {content?.ctaBlock.text && (
        <section id="nav-light" className={classes["cta-section"]}>
          <div className={`${classes["circle"]} ${classes["circle--4"]}`}></div>
          <div className={`${classes["circle"]} ${classes["circle--3"]}`}></div>
          <div className={`${classes["circle"]} ${classes["circle--2"]}`}></div>
          <div className={`${classes["circle"]} ${classes["circle--1"]}`}></div>

          <div className={classes.content}>
            <h2>{content.ctaBlock?.title}</h2>
            <span>{content.ctaBlock?.text}</span>
            <ScrollLink smooth={true} className={classes.button} to="form">
              {content.ctaBlock?.buttonText}
            </ScrollLink>
          </div>
        </section>
      )}

      {content?.videoBlock.video && (
        <section
          className={`section--white ${classes["video-section"]}`}
          id="nav-dark"
        >
          <div className={classes.content}>
            <div className={classes["block--1"]}>
              {content.videoBlock?.block1?.title && (
                <h3 className={classes.title}>
                  {content.videoBlock.block1.title}
                </h3>
              )}
              {content.videoBlock?.block1?.text && (
                <p className={classes.text}>{content.videoBlock.block1.text}</p>
              )}
            </div>

            {content.videoBlock.video && (
              <video className={classes.video} controls>
                <source src={content.videoBlock.video} />
              </video>
            )}

            {content.videoBlock.block2 && (
              <div className={classes["block--2"]}>
                {content.videoBlock.block2.title && (
                  <h3 className={classes.title}>
                    {content.videoBlock.block2.title}
                  </h3>
                )}
                {content.videoBlock.block2.text && (
                  <p className={classes.text}>
                    {content.videoBlock.block2.text}
                  </p>
                )}
              </div>
            )}
          </div>

          <FormCard id="form" />
        </section>
      )}
      {content?.markdown && (
          <section className="section--white">
            <div className={classes["content-container"]}>
              <div className={classes.markdown}>
                <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                  {content.markdown}
                </ReactMarkdown>
              </div>
            </div>
          </section>
      )}
    </main>
  );
};

export default Blog;
